import { db } from '@app/icare/client/db'
import type { ResultTypeFromQueryResult } from '@app/icare/client/db/client/localQueryOptions'
import { localPaginatedQueryOptions, localResultQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { relation } from '@app/icare/client/db/helpers'
import PatientStatusBadge from '@app/icare/components/status/PatientStatusBadge'
import { patientRelations } from '@app/icare/db/query/patientRelations'
import { queryKit } from '@app/icare/db/query/queryKit'
import { adTeamSelectQuery } from '@app/icare/entities/adTeam/queries/adTeam'
import type { AdTeamIdType } from '@app/icare/entities/adTeam/schema/ADTeam'
import { PatientEnrollmentDrawer } from '@app/icare/entities/patient/components/PatientEnrollmentDrawer'
import PatientSummary from '@app/icare/entities/patient/components/PatientSummary'
import { PatientStatusEnum } from '@app/icare/entities/patient/schema/Patient'
import { entityRegistry } from '@app/icare/entities/registry'
import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import ActiveEditors from '@app/icare/entities/user/components/ActiveEditors'
import { userSelectQuery } from '@app/icare/entities/user/queries/user'
import type { UserIdType } from '@app/icare/entities/user/schema/User'
import { setInputValue } from '@app/icare/module/tour/domHelpers'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { AddButton, ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Divider, Space, Table, Tour } from 'antd'
import { sql } from 'kysely'
import { useRef, useState } from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z.enum(['patient.personal.lastName', 'patient.status.value']).default('patient.personal.lastName').nullish(),
  'patient.status.value': Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
  careManagers: Q.in(z.custom<UserIdType>()).nullish(),
  adTeams: Q.in(z.custom<AdTeamIdType>()).nullish(),
  'therapies.type': Q.in(z.enum(TherapyTypeEnum.$.zodEnum())).nullish(),
  'patient.personal.birthdate': Q.between(z.iso.date()).nullish(),
})

const usePatientsQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: {
      'patient.status.value': F.in([PatientStatusEnum.open, PatientStatusEnum.new]),
    },
  },
})

export const Route = createFileRoute('/_app/patienten/')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: t`iCare - Patientenübersicht`,
      },
    ],
  }),
})

const { label } = entityRegistry.patient
function RouteComponent() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const tableRef = useRef<HTMLDivElement>(null)
  const filtersRef = useRef<HTMLDivElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const searchWrapperRef = useRef<HTMLDivElement>(null)

  const tour = useRouteTour('patients-list', [
    {
      title: t`Patientenübersicht`,
      description: t`Hier siehst du eine Tabelle mit deinen relevanten Datensätzen.`,
      target: () => tableRef.current,
    },
    {
      title: t`Filter`,
      description: t`Oben findest du Filter und die Suche. Standardmäßig werden nur aktive Einträge angezeigt.`,
      target: () => filtersRef.current,
    },
    {
      title: t`Patient hinzufügen`,
      description: t`Über diesen Button können neue Patient:innen hinzugefügt werden.`,
      target: () => addButtonRef.current,
      placement: 'bottom',
    },
    {
      title: t`Suche`,
      description: t`Suchen wir nach dem Patienten "Wizard Test Patient". Die Suche wird automatisch angewendet.`,
      target: () => searchWrapperRef.current,
      onEnter: () => {
        const input = searchWrapperRef.current?.querySelector<HTMLInputElement>('input')
        setInputValue(input ?? null, 'Wizard Test Patient')
      },
    },
    {
      title: t`Akte öffnen`,
      description: t`Über diesen Stift öffnest du die Patientenakte im Bearbeitungsmodus.`,
      target: () => tableRef.current?.querySelector<HTMLElement>('.ant-table-tbody tr:first-child') ?? null,
    },
  ])

  const queryStore = usePatientsQueryStore(useShallow(s => s))

  const patientsParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const careManagerFilter = patientsParams.filters.find(f => f.field === 'careManagers')
  const careManagerValues: UserIdType[] =
    careManagerFilter?.operator === 'in' && Array.isArray(careManagerFilter.value)
      ? careManagerFilter.value.flatMap(value => (typeof value === 'string' ? [value as UserIdType] : []))
      : careManagerFilter?.operator === 'eq' && typeof careManagerFilter.value === 'string'
        ? [careManagerFilter.value as UserIdType]
        : []

  const adTeamFilter = patientsParams.filters.find(f => f.field === 'adTeams')
  const adTeamValues: AdTeamIdType[] =
    adTeamFilter?.operator === 'in' && Array.isArray(adTeamFilter.value)
      ? adTeamFilter.value.flatMap(value => (typeof value === 'string' ? [value as AdTeamIdType] : []))
      : adTeamFilter?.operator === 'eq' && typeof adTeamFilter.value === 'string'
        ? [adTeamFilter.value as AdTeamIdType]
        : []

  const therapyFilter = patientsParams.filters.find(f => f.field === 'therapies.type')
  const therapyValues =
    therapyFilter?.operator === 'in' && Array.isArray(therapyFilter.value)
      ? therapyFilter.value.flatMap(value => (TherapyTypeEnum.$.is(value) ? [value] : []))
      : therapyFilter?.operator === 'eq' && TherapyTypeEnum.$.is(therapyFilter.value)
        ? [therapyFilter.value]
        : []

  const birthdateFromFilter = patientsParams.filters.find(
    f => f.field === 'patient.personal.birthdate' && f.operator === 'gte',
  )
  const birthdateFrom = typeof birthdateFromFilter?.value === 'string' ? birthdateFromFilter.value : undefined

  const birthdateToFilter = patientsParams.filters.find(
    f => f.field === 'patient.personal.birthdate' && f.operator === 'lte',
  )
  const birthdateTo = typeof birthdateToFilter?.value === 'string' ? birthdateToFilter.value : undefined

  const birthdateMismatchIndex =
    birthdateFrom && birthdateTo ? birthdateFrom.split('').findIndex((char, index) => char !== birthdateTo[index]) : -1

  const birthdatePrefix =
    birthdateFrom && birthdateTo
      ? birthdateFrom
          .slice(0, birthdateMismatchIndex === -1 ? birthdateFrom.length : birthdateMismatchIndex)
          .replace(/-$/, '')
      : undefined

  const patientsBaseParams = {
    ...patientsParams,
    filters: patientsParams.filters.filter(
      f =>
        f.field !== 'careManagers' &&
        f.field !== 'adTeams' &&
        f.field !== 'therapies.type' &&
        f.field !== 'patient.personal.birthdate',
    ),
  }

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      debug: true,
      query: db
        .selectFrom('patient')
        .selectAll('patient')
        .$call(queryKit.filters(patientsBaseParams))
        .$if(!!birthdatePrefix, qb =>
          qb.where(sql`json_extract("patient"."personal", '$.birthdate')`, 'like', `${birthdatePrefix}%`),
        )
        .$if(careManagerValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientCareManager')
                .select('patientCareManager.id')
                .whereRef('patientCareManager.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientCareManager'))
                .where('patientCareManager.toId', 'in', careManagerValues),
            ),
          ),
        )
        .$if(adTeamValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientAdTeam')
                .select('patientAdTeam.id')
                .whereRef('patientAdTeam.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientAdTeam'))
                .where('patientAdTeam.toId', 'in', adTeamValues),
            ),
          ),
        )
        .$if(therapyValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patientTherapy')
                .select('patientTherapy.id')
                .whereRef('patientTherapy.patientId', '=', 'patient.id')
                .$call(relation.isActive('patientTherapy'))
                .where('patientTherapy.type', 'in', therapyValues),
            ),
          ),
        )
        .$call(queryKit.search(patientsParams))
        .select([patientRelations.adTeams(), patientRelations.careManagers(), patientRelations.therapies()])
        .$call(queryKit.deleted(patientsParams.deleted, 'patient.deletedAt'))
        .$call(queryKit.paginate(patientsParams))
        .$call(queryKit.sort(patientsParams)),
      params: patientsParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: label,
        fixed: 'start',
        sorter: true,
        render: (_, row) => <FormattedPatientNameAndAddress patient={row} showStatusBadge={false} />,
      },
      {
        key: 'patient.status.value',
        title: <Trans>Status</Trans>,
        sorter: true,
        render: (_, row) => <PatientStatusBadge value={row.status.value} />,
      },
      {
        key: 'usersEditing',
        title: <Trans>aktuelle Bearbeitende</Trans>,
        sorter: false,
        render: (_, row) => (
          <ActiveEditors
            routeId="/_app/patienten/$id"
            routeIds={['/_app/patienten/$id/_overview/']}
            params={{ id: row.id }}
          />
        ),
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id"
            icon={<LinkIndicatorIcon />}
            iconPlacement="end"
            shape="round"
            params={{ id: record.id }}
            size="small"
          >
            <Trans>Anzeigen</Trans>
          </ButtonLink>
        ),
      },
    ],
    [],
    usePatientsQueryStore,
  )

  const careManagers = useQuery(
    localResultQueryOptions({
      query: userSelectQuery(),
    }),
  )

  const adTeams = useQuery(
    localResultQueryOptions({
      query: adTeamSelectQuery(),
    }),
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space separator={<Divider vertical />} wrap={false}>
            <Space size="small" ref={filtersRef}>
              <div ref={searchWrapperRef} className="flex-1">
                <SearchInput
                  size="small"
                  value={queryStore.meta.searchRawValue}
                  onChange={e => queryStore.setSearch(e.target.value)}
                />
              </div>
              <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
            </Space>
            <AddButton
              ref={addButtonRef}
              onClick={() =>
                void PatientEnrollmentDrawer.show().then(({ patientId }) => {
                  return navigate({ to: '/patienten/$id', params: { id: patientId }, ignoreBlocker: true })
                })
              }
            >
              {t`Patientenakte anlegen`}
            </AddButton>
          </Space>
        ),
        footer: <Divider size="small" style={{ marginBottom: 0 }} />,
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )
  return (
    <QueryFilterWrapper
      queryStore={usePatientsQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status.value',
          label: <Trans>Status</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options.map(o => ({ value: o.value, label: o.label })),
        },
        {
          name: 'patient.personal.birthdate',
          label: <Trans>Geburtsdatum</Trans>,
          mode: 'date',
          placeholder: t`z. B. 1951 oder 06.1951`,
        },
        {
          name: 'therapies.type',
          label: <Trans>Therapieformen</Trans>,
          mode: 'multiple',
          options: TherapyTypeEnum.$.options.map(o => ({ value: o.value, label: o.label })),
        },
        {
          name: 'adTeams',
          label: <Trans>AD-Teams</Trans>,
          mode: 'multiple',
          options: adTeams.data ?? [],
        },
        {
          name: 'careManagers',
          label: <Trans>Versorgungsmanager</Trans>,
          mode: 'multiple',
          options: careManagers.data ?? [],
        },
      ]}
    >
      <div data-testid="patient-list" ref={tableRef}>
        <Table
          dataSource={data?.data ?? []}
          loading={isFetching}
          columns={columns}
          size="small"
          rowKey="id"
          pagination={data}
          onChange={queryStore.onChange}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowRender: record => <PatientSummary patient={record} />,
          }}
        />
      </div>
      <Tour {...tour} />
    </QueryFilterWrapper>
  )
}
